import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "./Universita";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";

const TITLE = "Istituto Europa – Fondazione Gianni e Mario Profili";
const ADDR = "Via Forlanini 18, 07100 Sassari (SS), Italia";
const EMAIL = "uni.telematica@istitutoeuropasassari.it";
const TEL = "079 282 5126";
const PIVA = "02039840901";
const CF = "02039840901";
const REA = "206698";

export function PrivacyPolicy() {
  return (
    <LegalShell eyebrow="Informativa Privacy" title="Privacy Policy">
      <P>
        La presente Informativa è resa ai sensi dell'art. 13 del Regolamento UE
        2016/679 ("<b>GDPR</b>") e descrive le modalità con cui {TITLE},
        Titolare del trattamento, raccoglie e utilizza i dati personali degli
        utenti che visitano il sito o compilano i form di contatto e iscrizione
        del Polo ECP Profili di Sassari.
      </P>

      <H2>1. Titolare del trattamento</H2>
      <P>
        Titolare del trattamento è <b>{TITLE}</b>, con sede in {ADDR},
        P.IVA {PIVA} · C.F. {CF} · REA {REA}, contattabile all'indirizzo email <a href={`mailto:${EMAIL}`} className="text-brand-emerald font-semibold">{EMAIL}</a> o al numero {TEL}.
      </P>

      <H2>2. Tipologie di dati raccolti</H2>
      <Ul items={[
        "Dati identificativi (nome, cognome)",
        "Dati di contatto (email, numero di telefono, luogo di residenza)",
        "Informazioni accademiche di interesse (università e corso di interesse, modalità di iscrizione)",
        "Eventuali contenuti inseriti liberamente nei campi note del form",
        "Dati di navigazione e cookie tecnici (vedi Cookie Policy)",
      ]} />

      <H2>3. Finalità del trattamento</H2>
      <Ul items={[
        "Riscontrare le richieste di informazioni inviate tramite form",
        "Gestire la procedura di iscrizione ai corsi di laurea e ai master delle università convenzionate (Pegaso, Mercatorum, San Raffaele)",
        "Adempiere ad obblighi di legge fiscali, contabili e amministrativi",
        "Solo previo specifico consenso: invio di comunicazioni promozionali su nuovi corsi, master e convenzioni",
      ]} />

      <H2>4. Base giuridica</H2>
      <P>
        Il trattamento si fonda su (a) il <b>consenso</b> espresso dell'interessato (art. 6.1.a GDPR) per la gestione del form e per le comunicazioni promozionali; (b) l'<b>esecuzione di misure precontrattuali</b> e contrattuali (art. 6.1.b GDPR); (c) gli <b>obblighi di legge</b> a cui è soggetto il Titolare (art. 6.1.c GDPR).
      </P>

      <H2>5. Modalità del trattamento e conservazione</H2>
      <P>
        I dati vengono trattati con strumenti elettronici, conservati su database protetti da credenziali e backup periodici. I dati raccolti tramite form sono conservati per il tempo strettamente necessario al riscontro della richiesta e, in caso di iscrizione, per la durata del rapporto contrattuale aumentata dei termini di legge (10 anni per scritture contabili). I dati raccolti per finalità di marketing sono conservati fino alla revoca del consenso.
      </P>

      <H2>6. Destinatari dei dati</H2>
      <P>
        I dati possono essere comunicati a:
      </P>
      <Ul items={[
        "Personale autorizzato del Polo ECP Profili (tutor, segreteria)",
        "Università telematiche convenzionate, ai soli fini dell'iscrizione (Pegaso, Mercatorum, San Raffaele)",
        "Fornitori tecnici (hosting, invio email transazionali tramite Resend, Inc.) nominati Responsabili del trattamento ex art. 28 GDPR",
        "Autorità competenti, su richiesta e in adempimento di obblighi di legge",
      ]} />

      <H2>7. Trasferimento dati extra-UE</H2>
      <P>
        I dati possono essere trasferiti negli Stati Uniti per il solo invio di email transazionali (Resend, Inc.), sulla base delle Standard Contractual Clauses approvate dalla Commissione Europea.
      </P>

      <H2>8. Diritti dell'interessato</H2>
      <P>
        L'interessato può esercitare in ogni momento i diritti previsti dagli artt. 15-22 GDPR: accesso, rettifica, cancellazione ("diritto all'oblio"), limitazione, portabilità, opposizione e revoca del consenso. Per esercitare tali diritti è sufficiente scrivere a <a href={`mailto:${EMAIL}`} className="text-brand-emerald font-semibold">{EMAIL}</a>.
      </P>
      <P>
        È inoltre diritto dell'interessato proporre reclamo all'<b>Autorità Garante per la Protezione dei Dati Personali</b> (www.garanteprivacy.it).
      </P>

      <H2>9. Conferimento dei dati</H2>
      <P>
        Il conferimento dei dati contrassegnati come obbligatori nei form è necessario per ricevere risposta. Il mancato conferimento comporta l'impossibilità di gestire la richiesta.
      </P>

      <H2>10. Modifiche</H2>
      <P>
        La presente Informativa può essere aggiornata. Ultima revisione: dicembre 2025.
      </P>

      <ContactBox />
    </LegalShell>
  );
}

export function CookiePolicy() {
  return (
    <LegalShell eyebrow="Cookie Policy" title="Informativa Cookie">
      <P>
        Il sito ECP Profili Sassari utilizza cookie e tecnologie simili per
        garantire il corretto funzionamento delle pagine e per migliorare
        l'esperienza dell'utente. Di seguito i dettagli, ai sensi del
        Provvedimento del Garante Privacy del 10 giugno 2021 e del Reg. UE
        2016/679.
      </P>

      <H2>1. Cosa sono i cookie</H2>
      <P>
        I cookie sono piccoli file di testo memorizzati dal browser sul
        dispositivo dell'utente quando visita un sito web. Possono essere
        utilizzati per scopi tecnici, statistici o di profilazione.
      </P>

      <H2>2. Tipologie di cookie utilizzati</H2>
      <H3>2.1 Cookie tecnici (sempre attivi)</H3>
      <P>
        Indispensabili per il funzionamento del sito (sessione, preferenze
        utente, gestione del consenso). Per questi cookie non è richiesto
        consenso (art. 122 D.lgs. 196/2003 e provv. Garante).
      </P>

      <H3>2.2 Cookie analitici (richiedono consenso)</H3>
      <P>
        Il sito utilizza <b>PostHog</b> per raccogliere in forma aggregata e
        anonimizzata informazioni sull'utilizzo del sito, al fine di
        migliorarlo. Gli IP vengono anonimizzati. Questi cookie vengono
        installati solo previo consenso dell'utente tramite il banner di
        consenso.
      </P>

      <H3>2.3 Cookie di terze parti</H3>
      <Ul items={[
        "Google Maps – per la visualizzazione della mappa nella pagina Contatti (informativa: policies.google.com/privacy)",
        "Resend – per l'invio di email transazionali (resend.com/legal/privacy-policy)",
      ]} />

      <H2>3. Gestione del consenso</H2>
      <P>
        L'utente può accettare, rifiutare o personalizzare il consenso ai cookie
        tramite il banner che compare al primo accesso. Le preferenze possono
        essere modificate in qualsiasi momento tramite il pulsante "Preferenze
        Cookie" disponibile nel footer del sito.
      </P>

      <H2>4. Come disabilitare i cookie dal browser</H2>
      <P>
        È sempre possibile disabilitare i cookie direttamente dalle impostazioni
        del proprio browser (Chrome, Firefox, Safari, Edge). La disabilitazione
        dei cookie tecnici potrebbe pregiudicare il corretto funzionamento del
        sito.
      </P>

      <H2>5. Modifiche</H2>
      <P>Ultima revisione: dicembre 2025.</P>

      <ContactBox />
    </LegalShell>
  );
}

export function Gdpr() {
  return (
    <LegalShell eyebrow="Trattamento Dati · GDPR" title="Informativa GDPR">
      <P>
        Questa pagina riassume in forma sintetica gli adempimenti GDPR di {TITLE} con riferimento al sito web e ai trattamenti effettuati nel Polo ECP Profili di Sassari. Per il dettaglio completo si rimanda alla <Link className="text-brand-emerald font-semibold" to="/privacy-policy">Privacy Policy</Link>.
      </P>

      <H2>1. Titolare del trattamento</H2>
      <P>{TITLE} – {ADDR} – P.IVA {PIVA} · C.F. {CF} · REA {REA} – {EMAIL} – {TEL}</P>

      <H2>2. Responsabile della Protezione dei Dati (DPO)</H2>
      <P>
        Per qualsiasi richiesta relativa alla protezione dei dati personali, è possibile contattare il referente privacy all'indirizzo <a href={`mailto:${EMAIL}`} className="text-brand-emerald font-semibold">{EMAIL}</a>.
      </P>

      <H2>3. Diritti dell'interessato (artt. 15-22 GDPR)</H2>
      <Ul items={[
        "Accesso ai propri dati personali",
        "Rettifica dei dati inesatti",
        "Cancellazione (diritto all'oblio)",
        "Limitazione del trattamento",
        "Portabilità dei dati",
        "Opposizione al trattamento",
        "Revoca del consenso in qualsiasi momento",
        "Reclamo al Garante Privacy (www.garanteprivacy.it)",
      ]} />

      <H2>4. Sicurezza</H2>
      <P>
        Adottiamo misure tecniche e organizzative adeguate (cifratura HTTPS, accessi limitati e tracciati, backup, password sicure) per proteggere i dati personali da accessi non autorizzati, perdita o distruzione.
      </P>

      <H2>5. Modulo per esercitare i diritti</H2>
      <P>
        Per esercitare uno dei diritti elencati è sufficiente inviare una email a <a href={`mailto:${EMAIL}`} className="text-brand-emerald font-semibold">{EMAIL}</a> indicando il diritto che si intende esercitare e allegando un documento d'identità per la verifica. Risponderemo entro 30 giorni.
      </P>

      <ContactBox />
    </LegalShell>
  );
}

// ── shared ──
function LegalShell({ eyebrow, title, children }) {
  return (
    <div data-testid={`page-legal-${eyebrow.toLowerCase().replace(/\s/g, "-")}`}>
      <PageHero eyebrow={eyebrow} title={title} />
      <article className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">{children}</div>
      </article>
    </div>
  );
}
const H2 = ({ children }) => <h2 className="font-heading font-extrabold text-2xl text-brand-navy mt-10 mb-3">{children}</h2>;
const H3 = ({ children }) => <h3 className="font-heading font-bold text-lg text-brand-navy mt-6 mb-2">{children}</h3>;
const P = ({ children }) => <p className="text-brand-ink text-[16px] leading-[1.8] mb-3">{children}</p>;
const Ul = ({ items }) => (
  <ul className="space-y-2 mb-3 ml-1">
    {items.map((i, idx) => (
      <li key={idx} className="text-brand-ink text-[16px] leading-relaxed flex gap-2">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-emerald shrink-0" /> {i}
      </li>
    ))}
  </ul>
);
const ContactBox = () => (
  <div className="mt-12 rounded-3xl bg-brand-surface border border-brand-line p-7">
    <div className="eyebrow text-brand-emerald mb-2">Contatti per esercizio dei diritti</div>
    <div className="grid sm:grid-cols-3 gap-4 text-sm">
      <div className="flex gap-2 items-start"><MapPin className="w-4 h-4 text-brand-emerald shrink-0 mt-0.5" /> {ADDR}</div>
      <div className="flex gap-2 items-start"><Phone className="w-4 h-4 text-brand-emerald shrink-0 mt-0.5" /> {TEL}</div>
      <div className="flex gap-2 items-start break-all"><Mail className="w-4 h-4 text-brand-emerald shrink-0 mt-0.5" /> {EMAIL}</div>
    </div>
    <Link to="/contatti" className="btn-primary mt-6 inline-flex text-sm" data-testid="legal-back-cta">
      Torna al modulo di contatto <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);
